import React from "react";

/**
 * The count chips both tabs put on a collapsed parent row.
 *
 * Hand-built rather than Vibe's `Counter`, which can only render `prefix + count` — so
 * "5 issues" is not expressible by it at all, and the badge it replaced had to sandwich a red
 * Counter between two loose text nodes to get the wording it wanted.
 *
 * Shared so the Items and Subitems tabs cannot drift apart on it: a third copy is how the two
 * tabs came to disagree about value rendering in the first place.
 */
const CHIP_BASE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: 22,
  padding: "0 var(--spacing-small)",
  borderRadius: 11,
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
};

/** Context, not alarm — a count that only matters beside the one next to it. */
export const QUIET_CHIP: React.CSSProperties = {
  ...CHIP_BASE,
  border: "1px solid var(--layout-border-color)",
  color: "var(--secondary-text-color)",
};

/** The number that needs attention. */
export const ALARM_CHIP: React.CSSProperties = {
  ...CHIP_BASE,
  background: "var(--negative-color)",
  color: "var(--text-color-on-brand)",
};

/**
 * "We checked, and this one is fine." Both tabs previously rendered NOTHING in this case,
 * which made a clean parent indistinguishable from one whose scan had not come back — the two
 * most different outcomes in the list, identical in the default collapsed view.
 */
export const CLEAN_CHIP: React.CSSProperties = {
  ...CHIP_BASE,
  background: "var(--positive-color-selected)",
  color: "var(--primary-text-color)",
};

/**
 * Ground for an OPEN parent row. Mixed one step deeper than --allgrey because the group bands
 * inside an open row already own --allgrey, and the same grey at two depths reads as one depth.
 * Mixed from tokens rather than hardcoded so it still follows a theme switch.
 */
export const OPEN_ROW_GROUND =
  "color-mix(in srgb, var(--allgrey-background-color) 76%, var(--layout-border-color))";
